import { Box, Button, Card, CardContent, Typography, TextField, Grid, Paper } from '@mui/material'
import { useState, useEffect } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import InfoIcon from '@mui/icons-material/Info'
import { useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'

// Custom Component ย่อย
import LandRegistrationInfoModal from './ChildComponent/LandRegistrationInfoModal'
// Custom Hooks
import useLandregistration from './hooks/useLandregistration'

export default function LandRegistration() {
    // SearchParams สำหรับจดจำประวัติการค้นหาบน URL บราวเซอร์
    let [searchParams, setSearchParams] = useSearchParams()

    const [search, setSearch] = useState({
        parcelCode: searchParams.get('parcelCode') || '',
        oldParcel: searchParams.get('oldParcel') || '',
        dn: searchParams.get('dn') || ''
    })

    const [rowData, setRowData] = useState(undefined)
    const [registrationData, setRegistrationData] = useState([])
    const [infoModalOpen, setInfoModalOpen] = useState(false)

    // เรียกใช้ Custom Hook ตัวแรงของเรา
    const { fetchGisParcelData } = useLandregistration()

    // 📌 แก้ไขบล็อกตัวสเปกคอลัมน์ในไฟล์ LandRegistrationInfoModal.js ให้ตรงกับตารางใหม่ MySQL
    const columns = [
        { field: 'id', headerName: 'ลำดับ', width: 70 },
        {
            field: 'owner',
            headerName: 'เจ้าของปัจจุบัน (ผู้รับโอน)',
            width: 200,
            // 🟢 ดึงค่าแมปตามฟิลด์ใหม่แกะกล่องบน MySQL
            valueGetter: (params) => {
                const { new_owner_prefix, new_owner_firstname, new_owner_lastname } = params.row;
                return `${new_owner_prefix || ''}${new_owner_firstname || ''} ${new_owner_lastname || ''}`.trim() || '-';
            }
        },
        {
            field: 'previous_owner',
            headerName: 'เจ้าของเดิม (ผู้โอน)',
            width: 200,
            // 🟢 ดึงค่าแมปตามฟิลด์ใหม่แกะกล่องบน MySQL
            valueGetter: (params) => {
                const { old_owner_prefix, old_owner_firstname, old_owner_lastname } = params.row;
                return `${old_owner_prefix || ''}${old_owner_firstname || ''} ${old_owner_lastname || ''}`.trim() || '-';
            }
        },
        {
            field: 'new_dn',
            headerName: 'เลขที่เอกสารสิทธิ์ใหม่',
            width: 150,
            valueGetter: (params) => params.row.new_dn || '-'
        },
        {
            field: 'dn',
            headerName: 'เลขที่เอกสารสิทธิ์เดิม',
            width: 150,
            valueGetter: (params) => params.row.dn || '-'
        },
        {
            field: 'note',
            headerName: 'หมายเหตุ',
            width: 180,
            valueGetter: (params) => params.row.note || '-'
        },
        {
            field: 'action',
            headerName: 'การจัดการ',
            sortable: false,
            width: 150,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<InfoIcon />}
                        onClick={() => {
                            setRowData(params.row);
                            setInfoModalOpen(true);
                        }}
                    >
                        ดูประวัติสารบบ
                    </Button>
                )
            }
        }
    ]

    // 🟢 ดึงข้อมูลเริ่มต้นตอนเปิดโหลดหน้าเว็บครั้งแรก
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                Swal.fire({
                    title: 'กำลังโหลดข้อมูลแผนที่...',
                    allowOutsideClick: false,
                    didOpen: () => { Swal.showLoading(); }
                });

                // ยิงคำขอเรียกข้อมูลเริ่มต้น (ถ้ามี query ค้างบน URL ก็ส่งไปด้วย)
                await fetchGisParcelData(setRegistrationData, search.parcelCode, search.oldParcel, search.dn);
                Swal.close();
            } catch (err) {
                console.error(err);
                Swal.fire({ icon: 'error', title: 'ดึงข้อมูลล้มเหลว', text: 'ไม่สามารถติดต่อเซิร์ฟเวอร์ระบบ GIS ได้' });
            }
        };
        loadInitialData();
    }, []);

    // 🟢 ฟังก์ชันหลักเมื่อกดปุ่มค้นหาข้อมูล
    const handleSearchSubmit = async (event) => {
        event.preventDefault();

        // อัปเดตประวัติการกรอกลงในเส้นทาง URL บราวเซอร์ เพื่อให้กดแชร์ลิงก์หรือกด Refresh หน้าแล้วค่าไม่หาย
        if (search.parcelCode || search.oldParcel || search.dn) {
            setSearchParams({
                parcelCode: search.parcelCode,
                oldParcel: search.oldParcel,
                dn: search.dn
            });
        } else {
            setSearchParams({}); // เคลียร์พารามิเตอร์ถ้าช่องเสิร์ชว่างเปล่า
        }

        try {
            Swal.fire({
                title: 'กำลังค้นหาข้อมูล...',
                text: 'กรุณารอซักครู่',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });

            await fetchGisParcelData(setRegistrationData, search.parcelCode, search.oldParcel, search.dn);
            Swal.close();

        } catch (err) {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'ค้นหาผิดพลาด', text: 'เกิดปัญหาระหว่างจัดส่งคำค้นหา' });
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f4f6f8' }}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                    {/* หัวข้อหน้าจอเด่นๆ */}
                    <Typography variant='h5' fontWeight="bold" color="primary.main" mb={3}>
                        🗺️ ระบบทะเบียนคุมการเปลี่ยนแปลงข้อมูลแปลงที่ดิน (GIS & LTAX)
                    </Typography>

                    {/* 🟢 โซนฟอร์มค้นหา: ใช้ Grid 2 แถว คลีน กระชับ ไม่ล้นหน้าจอ */}
                    <Paper
                        variant="outlined"
                        component="form"
                        onSubmit={handleSearchSubmit}
                        sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    fullWidth
                                    id="parcelCode"
                                    label="รหัสแปลงที่ดิน"
                                    variant="outlined"
                                    size="small"
                                    value={search.parcelCode}
                                    onChange={(e) => setSearch({ ...search, parcelCode: e.target.value.toUpperCase() })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    fullWidth
                                    id="oldParcel"
                                    label="รหัสแปลงที่ดินเก่า"
                                    variant="outlined"
                                    size="small"
                                    value={search.oldParcel}
                                    onChange={(e) => setSearch({ ...search, oldParcel: e.target.value.toUpperCase() })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    fullWidth
                                    id="dn"
                                    label="เลขที่โฉนด"
                                    variant="outlined"
                                    size="small"
                                    value={search.dn}
                                    onChange={(e) => setSearch({ ...search, dn: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={3} sx={{ textAlign: { xs: 'right', md: 'left' } }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    type='submit'
                                    fullWidth
                                    startIcon={<SearchIcon />}
                                    sx={{ height: 40 }}
                                >
                                    ค้นหาแปลงที่ดิน
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* 🟢 โซนแสดงผลตาราง DataGrid หลัก */}
                    <div style={{ height: 600, width: '100%' }}>
                        <DataGrid
                            rows={registrationData}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            disableSelectionOnClick
                            autoHeight={false}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* หน้าต่างสารบบโมดอลย่อยตัวสวยของเรา */}
            <LandRegistrationInfoModal
                infoModalOpen={infoModalOpen}
                setInfoModalOpen={setInfoModalOpen}
                rowData={rowData}
            />
        </Box>
    )
}